import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * Shared colored status block for "Active"/status columns in material-react-table.
 *
 * Mirrors the large colored "sentiment" block used in the conversations table
 * (full padding + ~90% width) so the chip is the same size in every portal
 * table. Centralizing it here keeps the styling maintainable.
 *
 * Two ways to use it:
 *  - Boolean shorthand (renders Active/Inactive with success/error colors):
 *      Cell: ({ cell }) => <MRTActiveStatusCell active={cell.getValue() !== true} />
 *  - Explicit label + color (e.g. a multi-state lifecycle status):
 *      <MRTActiveStatusCell label={status} backgroundColor={getStatusColor(status, theme)} />
 *
 * `activeLabel` / `inactiveLabel` override the boolean-shorthand text.
 */
const MRTActiveStatusCell = ({
  active,
  label = null,
  backgroundColor = null,
  activeLabel = "Active",
  inactiveLabel = "Inactive",
}) => {
  const theme = useTheme();

  const resolvedBackgroundColor =
    backgroundColor != null
      ? backgroundColor
      : active
        ? theme.palette.success.main
        : theme.palette.error.main;

  const resolvedLabel = label != null ? label : active ? activeLabel : inactiveLabel;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Box
        sx={{
          backgroundColor: resolvedBackgroundColor,
          color: theme.palette.getContrastText(resolvedBackgroundColor),
          borderRadius: "0.25rem",
          padding: theme.spacing(1.5),
          width: "90%",
          boxSizing: "border-box",
          // Center the label both horizontally and vertically in the box.
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          // Keep the status word (e.g. "ACTIVE" / "SCHEDULED") on a single line.
          whiteSpace: "nowrap",
        }}
      >
        <Typography variant="button" sx={{ fontWeight: "bold", lineHeight: 1 }}>
          {resolvedLabel}
        </Typography>
      </Box>
    </Box>
  );
};

export default MRTActiveStatusCell;
